"use client";

/**
 * Providers Composition
 * Wraps the app with all necessary providers in the correct order
 */

// Buffer polyfill for Solana/Anchor (required for browser compatibility)
import { Buffer } from "buffer";
if (typeof window !== "undefined") {
  window.Buffer = Buffer;
}

import { PrivyProvider } from "@privy-io/react-auth";
import { toSolanaWalletConnectors } from "@privy-io/react-auth/solana";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HeroUIProvider } from "@heroui/react";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { Toaster } from "sonner";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/contexts/auth-context";
import { ReferralConsumer } from "@/components/ReferralConsumer";
import { ModalProvider } from "@/contexts/modal-context";
import { useRouter } from "next/navigation";
import PlausibleProvider from "next-plausible";

export function Providers({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  return (
    <PlausibleProvider
      customDomain="https://analytics.pulsar.money"
      domain="portal.yiedl.ai"
    >
      <PrivyProvider
        appId={"cmdonap9700d3ky0jcrppiz4x"}
        config={{
          // Only allow wallet login (Solana wallets only)
          loginMethods: ["wallet"],

          // Appearance configuration
          appearance: {
            theme: "dark",
            accentColor: "#33D46E", // Yiedl primary green
            logo: undefined, // Add logo later

            // CRITICAL: Set to Solana-only mode
            walletChainType: "solana-only",

            // Configure which wallets to show
            walletList: [
              "detected_solana_wallets", // Show any detected Solana wallets first
              "phantom",
              "solflare",
              "backpack",
            ],
          },

          // Configure Solana external wallets
          externalWallets: {
            solana: {
              connectors: toSolanaWalletConnectors(),
            },
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ModalProvider>
              <HeroUIProvider navigate={(path) => router.push(path)}>
                <NextThemesProvider attribute="class" defaultTheme="light">
                  <ReferralConsumer />
                  <Toaster />
                  {children}
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-right"
                  />
                </NextThemesProvider>
              </HeroUIProvider>
            </ModalProvider>
          </AuthProvider>
        </QueryClientProvider>
      </PrivyProvider>
    </PlausibleProvider>
  );
}
